/**
 * Class description
 *
 * Created: 2018-03-13
 * @author jonas
 */
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {MathHelper} from "@nolimitcity/slot-game/bin/core/utils/MathHelper";
import {WinRatioType} from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";
import {Back, Elastic, Linear, Power2, TimelineLite, TweenLite} from "gsap";
import {DirtyWildsParticleConfig} from "../../config/DirtyWildsParticleConfig";
import {DirtyWildsTextStyles} from "../../config/DirtyWildsTextStyles";
import {DirtyWildGameModuleConfig} from "../DirtyWildGameModuleConfig";
import {DirtyWildsGameAssets} from "../DirtyWildsGameAssets";
import {DirtyWildsGameConfig, IDirtyWildWinRatioConfig} from "../DirtyWildsGameConfig";
import {DirtyWildsSoundConfig} from "../DirtyWildsSoundConfig";
import {DualLayerBitmapCountUp} from "../display/DualLayerBitmapCountUp";
import {TweenParticleEmitter} from "../display/TweenParticleEmitter";


export class WinCountUpDisplay extends PIXI.Container {
    private _scaleContainer:PIXI.Container;
    private _countText:DualLayerBitmapCountUp;
    private _hasStopped:boolean;
    private _coinParticles:TweenParticleEmitter;
    private _multiplierText:PIXI.BitmapText;

    private _startTargetScale:number = 0.6;
    private _postMultiplyTargetScale:number = 0.9;
    private _finalTargetScale:number = 1;
    private _multiplier:number = 0;

    constructor() {
        super();
        this.initAnimations();
    }

    protected initAnimations() {
        const winDisplayPos = new PIXI.Point(360, 340);

        this._scaleContainer = new PIXI.Container();
        this._scaleContainer.position.set(winDisplayPos.x, winDisplayPos.y);
        this._scaleContainer.alpha = 0;

        this._countText = new DualLayerBitmapCountUp(DirtyWildsTextStyles.WIN_DISPLAY_NUMBERS_WHITE, DirtyWildsTextStyles.WIN_DISPLAY_NUMBERS, 700);
        this._countText.getFadeOutTopLayer(0.01);

        this._multiplierText = new PIXI.BitmapText("X1", DirtyWildsTextStyles.WIN_DISPLAY_MULTIPLIER_TEXT);
        this._multiplierText.anchor = new PIXI.Point(0.5, 0.8);
        this._multiplierText.position.set(0, -this._countText.height * 0.5);
        this._multiplierText.scale.set(0, 0);
        this._multiplierText.alpha = 0;


        const coinParticleTextures = [
            GameResources.getTextures(DirtyWildsGameAssets.WIN_DISPLAY_PARTICLE_1)[0],
            GameResources.getTextures(DirtyWildsGameAssets.WIN_DISPLAY_PARTICLE_2)[0],
            GameResources.getTextures(DirtyWildsGameAssets.WIN_DISPLAY_PARTICLE_3)[0],
            GameResources.getTextures(DirtyWildsGameAssets.WIN_DISPLAY_PARTICLE_4)[0]
        ];

        this._coinParticles = new TweenParticleEmitter(coinParticleTextures, DirtyWildsParticleConfig.winDisplayParticleConfig);
        this._coinParticles.position.set(winDisplayPos.x, winDisplayPos.y);


        const multiplierParticleTextures = [
            GameResources.getTextures(DirtyWildsGameAssets.MULTIPLIER_PARTICLE_1)[0]
        ];

        // this._multiplierParticles = new TweenParticleEmitter(multiplierParticleTextures, DirtyWildsParticleConfig.winMultiplierParticleConfig);
        // this._multiplierParticles.position.set(winDisplayPos.x, winDisplayPos.y - 100);

        this._scaleContainer.addChild(this._countText);
        this._scaleContainer.addChild(this._multiplierText);
        this.addChild(this._coinParticles);
        // this.addChild(this._multiplierParticles);
        this.addChild(this._scaleContainer);
    }

    private getShowMultiplier(multiplier:number, duration:number):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        if(multiplier < 2) {
            return tl;
        }

        // #231
        if (!DirtyWildsGameConfig.instance.WIN_BELOW_STAKE) {
            tl.add(() => SlotGame.sound.playEffect(DirtyWildsSoundConfig.COUNT_UP_MULTIPLIER));
        }
        tl.add([
            () => this._multiplierText.text = "X" + multiplier,
            new TweenLite(this._multiplierText, 0.01, {alpha : 1, ease : Linear.easeNone}),
            new TweenLite(this._multiplierText.scale, 0.01, {x : 1, y : 1, ease : Power2.easeIn})
        ]);
        // tl.add(() => this.getMultiplierCoinToss(multiplier));


        return tl;
    }

    private getHideMultiplier(duration:number):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        this._multiplierText.text = "X" + this._multiplier;
        tl.add([
            new TweenLite(this._multiplierText, duration * 0.3, {alpha : 0, ease : Linear.easeNone}),
            new TweenLite(this._multiplierText.scale, duration, {x : 0, y : 0, ease : Back.easeOut})
        ]);


        return tl;
    }

    public getWinDisplayWithMultiplier(from:number, normalWin:number, multipliedWin:number, totalDuration:number, multiplier:number, multiplierDur:number, winConfig:IDirtyWildWinRatioConfig, isBigWin:boolean, isPotrait:boolean):TimelineLite {
        this._multiplier = multiplier;
        this._hasStopped = false;
        this._finalTargetScale = 1;
        // totalDuration -= multiplierAnimation.duration();

        let preMultiplierCountDuration:number = totalDuration * 0.5;
        const postMultiplierCountDuration:number = totalDuration * 0.5;
        let startScale:number = totalDuration > 0.4 ? this._startTargetScale : 0.2;

        const showDuration:number = 0.3;
        const midScale:number = totalDuration > 0.4 ? this._postMultiplyTargetScale : 1;
        const postMultiplyScaleDuration:number = totalDuration > 0.4 ? postMultiplierCountDuration : 0.1;


        const isMultiplied:boolean = multiplier > 1;
        if(!isMultiplied) {
            normalWin *= 0.5;
        }


        const tl:TimelineLite = new TimelineLite();
        const defaultScale = 1;


        const showTl:TimelineLite = new TimelineLite();
        showTl.add(() => this._scaleContainer.scale.set(startScale, startScale));
        showTl.add(() => this._scaleContainer.alpha = 1);


        const scaleTl:TimelineLite = new TimelineLite();
        scaleTl.add(showTl);
        scaleTl.add(new TweenLite(this._scaleContainer.scale, postMultiplyScaleDuration, {
            x : defaultScale * midScale,
            y : defaultScale * midScale,
            ease : Linear.easeIn
        }));


        const count:TimelineLite = new TimelineLite();

        // #231
        if (!DirtyWildsGameConfig.instance.WIN_BELOW_STAKE) {
            count.add(() => SlotGame.sound.playEffect(isBigWin ? DirtyWildsSoundConfig.COUNT_UP_LOOP_BIWIN : DirtyWildsSoundConfig.COUNT_UP_LOOP, -1));
        }


        if(isMultiplied && !isBigWin) {
            count.add(this._countText.getCountAnimation(from, normalWin, preMultiplierCountDuration));
            count.add([() => {
                this._countText.stopAt(normalWin);
                SlotGame.sound.stopEffect(isBigWin ? DirtyWildsSoundConfig.COUNT_UP_LOOP_BIWIN : DirtyWildsSoundConfig.COUNT_UP_LOOP)
            }, () => this.stopCountAt(normalWin, winConfig, isBigWin)]);
            const levelTime:number = 0.5 * multiplier > 5 ? MathHelper.roundToDecimals(5 / multiplier, 2) : 0.5;
            for(let m:number = 1; m <= multiplier; m++) {
                const multiplierAnimation = this.getShowMultiplier(m, 0.01);
                const showNumber:number = normalWin * m;
                const scale:number = this.getScale(m, isPotrait);
                count.add(multiplierAnimation);
                count.add([
                    this._countText.getCountAnimation(showNumber, showNumber, 0.01),
                    TweenLite.to(this._scaleContainer.scale, levelTime / 2,
                        {
                            x : scale,
                            y : scale,
                            ease : Elastic.easeOut.config(2, 1)
                        }),
                ]);
                count.add(TweenLite.to(this, levelTime / 2, {}));
            }
        } else {
            count.add(this._countText.getCountAnimation(from, multipliedWin, totalDuration));
            count.add([
                new TweenLite(this._scaleContainer.scale, 0.04, {
                    x : defaultScale * this._finalTargetScale,
                    y : defaultScale * this._finalTargetScale,
                    ease : Linear.easeNone
                }),
                () => this.stopCountAt(multipliedWin, winConfig, isBigWin)
            ]);
        }
        tl.add([count, scaleTl]);

        return tl;
    }

    private getScale(multiplier:number, isPotrait:boolean):number {
        multiplier = isPotrait ? 1 : multiplier;
        let scale:number = 1 + ((multiplier - 1) * 0.1);
        this._finalTargetScale = scale > 1 ? scale : this._finalTargetScale;
        return scale;
    }

    public getEndAnimation(totalAmount:number, winConfig:IDirtyWildWinRatioConfig, delay:number = 0.5, isBigWin:boolean):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        tl.add([
            new TweenLite(this._scaleContainer.scale, 0.02, {
                x : this._finalTargetScale,
                y : this._finalTargetScale,
                ease : Power2.easeIn
            }),
            new TweenLite(this._scaleContainer, 0.02, {alpha : 1, ease : Linear.easeOut})
        ]);
        tl.add(() => this.stopCountAt(totalAmount, winConfig, isBigWin));
        tl.add([
            new TweenLite(this._scaleContainer.scale, 0.2, {x : 0, y : 0, ease : Power2.easeIn}),
            new TweenLite(this._scaleContainer, 0.2, {alpha : 0, ease : Linear.easeOut}),
            this.getHideMultiplier(0.2)
        ], delay);
        return tl;
    }

    private stopCountAt(to:number, winConfig:IDirtyWildWinRatioConfig, isBigWin:boolean) {
        if(!this._hasStopped) {
            this._hasStopped = true;
            this._countText.stopAt(to);
            SlotGame.sound.stopEffect(isBigWin ? DirtyWildsSoundConfig.COUNT_UP_LOOP_BIWIN : DirtyWildsSoundConfig.COUNT_UP_LOOP);
            if(isBigWin) {
                let bigWinEndSound:string = DirtyWildsSoundConfig.BIG_WIN_END_1;
                switch(winConfig.winIndex) {
                    case 1:
                        bigWinEndSound = DirtyWildsSoundConfig.BIG_WIN_END_1;
                        break;
                    case 2:
                        bigWinEndSound = DirtyWildsSoundConfig.BIG_WIN_END_2;
                        break;
                    case 3:
                        bigWinEndSound = DirtyWildsSoundConfig.BIG_WIN_END_3;
                        break;
                }
                DirtyWildGameModuleConfig.soundPlayer.stopBigWinLoop(bigWinEndSound);
            }

            this._countText.getFadeInAndOutTopLayer(0.4, 1, 0, 0.2);

            // #231
            if (!DirtyWildsGameConfig.instance.WIN_BELOW_STAKE) {
                this.getEndCoinToss(winConfig.type!);
            }

            // #231
            if(!isBigWin && !DirtyWildsGameConfig.instance.WIN_BELOW_STAKE) {
                SlotGame.sound.playEffect(DirtyWildsSoundConfig.COUNT_UP_END);
            }
        }
    }


    private getMultiplierCoinToss(multiplier:number) {
        const emitterSettings:{ scale:number, maxParticles:number } = this.getFrequencyForMultiplier(multiplier);
        /*        this._multiplierParticles.emitter.startScale = emitterSettings.scale;
                this._multiplierParticles.emitter.endScale = emitterSettings.scale;
                this._multiplierParticles.emitter.maxParticles = emitterSettings.maxParticles;*/
        // this._multiplierParticles.getAnimation();
    }

    private getFrequencyForMultiplier(multiplier:number):{ scale:number, maxParticles:number } {
        return {scale : 0.5, maxParticles : multiplier};
    }

    private getEndCoinToss(winType:WinRatioType) {
        const emitterSettings:{ scale:number, maxParticles:number } = this.getFrequencyForWinType(winType);
        this._coinParticles.getAnimation(emitterSettings);
    }

    private getFrequencyForWinType(winType:WinRatioType):{ scale:number, maxParticles:number } {
        switch(winType) {
            case WinRatioType.INFINITE_WIN :
                return {scale : 0.3, maxParticles : 90};

            case WinRatioType.SUPER_MEGA_WIN :
                return {scale : 0.3, maxParticles : 80};

            case WinRatioType.MEGA_WIN :
                return {scale : 0.3, maxParticles : 70};

            case WinRatioType.BIG_WIN :
                return {scale : 0.3, maxParticles : 60};

            case WinRatioType.LARGE :
                return {scale : 0.3, maxParticles : 50};

            case WinRatioType.MEDIUM :
                return {scale : 0.3, maxParticles : 40};

            case WinRatioType.SMALL :
                return {scale : 0.3, maxParticles : 30};

            default :
                return {scale : 0.3, maxParticles : 20};
        }
    }
}